/// <reference types="vite/client" />
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { type ComponentProps, type PropsWithChildren, useState } from "react";

import { createQueryClient } from "../api/queryClient";

// Cross-cutting providers compose here as they land:
//   <AuthProvider>
//     <FeatureFlagProvider>
//       <ThemeProvider>
//         <ToastProvider>{children}</ToastProvider>
//       </ThemeProvider>
//     </FeatureFlagProvider>
//   </AuthProvider>
// Wired once in App.tsx. Features do not wrap themselves.
//
// `QueryClientProviderChildren` borrows the children type that
// `<QueryClientProvider>` itself declares. TanStack Query's bundled
// `.d.ts` uses `import * as React_2 from "react"` and references
// `React_2.ReactNode`; when tsc resolves that import through the
// pnpm store it can land on a different `@types/react` copy than
// the one this file's `react` import resolves to. Aliasing here
// keeps the cast aligned with whatever copy TanStack itself sees.
type QueryClientProviderChildren = ComponentProps<typeof QueryClientProvider>["children"];

function AppProviders({ children }: PropsWithChildren) {
  const [queryClient] = useState(createQueryClient);

  return (
    <QueryClientProvider client={queryClient}>
      {children as QueryClientProviderChildren}
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}

export default AppProviders;
